import { streamSse, type ApiClient } from '@nyce/admin-kit/api';
import { client } from '../session';

// ---------- GCP logs (M3, docs/contracts.md) ----------

export const SEVERITIES = [
  'DEFAULT',
  'DEBUG',
  'INFO',
  'NOTICE',
  'WARNING',
  'ERROR',
  'CRITICAL',
  'ALERT',
  'EMERGENCY',
] as const;
export type Severity = (typeof SEVERITIES)[number];

export interface LogQuery {
  environmentId: string;
  /** Omitted = all of the environment's namespaces. */
  namespaces?: string[];
  /** RE2 regex, ≤ 200 chars. */
  pod?: string;
  container?: string;
  /** ≤ 500 chars. */
  text?: string;
  /** Omitted = all. */
  severities?: Severity[];
  from: string;
  to: string;
  /** 1–1000, default 200. */
  pageSize?: number;
  pageToken?: string;
}

export interface LogEntry {
  insertId: string;
  timestamp: string;
  /** Effective severity (after the JSON/text rescue). */
  severity: Severity;
  gcpSeverity: Severity;
  severitySource: 'gcp' | 'json' | 'text';
  namespace: string;
  pod: string;
  container: string;
  /** ≤ 8 KB. */
  message: string;
  /** Full JSON of the entry payload, ≤ 64 KB. */
  payload: string;
  logName: string;
}

export interface LogPage {
  /** Newest first. */
  entries: LogEntry[];
  nextPageToken: string | null;
  filter: string;
  consoleUrl: string;
}

export type TailEvent =
  | { event: 'entry'; data: LogEntry }
  | { event: 'suppressed'; data: { reason: string; count: number } }
  | { event: 'error'; data: { error: string; code?: string } }
  | { event: 'end'; data: { reason: string } };

export function logsApi(c: ApiClient) {
  return {
    query: (q: LogQuery, signal?: AbortSignal) => c.post<LogPage>('/logs/query', q, { signal }),
    /** Live tail over SSE; resolves when the server ends the stream or `signal` aborts. */
    tail: (
      q: Omit<LogQuery, 'from' | 'to' | 'pageSize' | 'pageToken'>,
      onEvent: (e: TailEvent) => void,
      signal: AbortSignal
    ) =>
      streamSse(c, 'POST', '/logs/tail', q, (event, data) => onEvent({ event, data } as TailEvent), signal),
  };
}

export const logs = logsApi(client);
